import _objectSpread2 from '../../node_modules/@babel/runtime/helpers/esm/objectSpread2.js';
import _objectWithoutProperties from '../../node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js';
import '../../node_modules/core-js/modules/web.dom-collections.iterator.js';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { COUNTRIES } from '../../constants/countries.js';
import { block } from '../../utils/block.js';
import { getAlpha2FromPhoneCode, getUserAlpha2 } from '../../utils/country.js';
import { applyPhoneMask } from '../../utils/phone.js';
import { PhoneInputList } from './PhoneInputList.js';
import './PhoneInput.scss.js';
import 'flag-icons/css/flag-icons.min.css';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Dropdown } from '../Dropdown/Dropdown.js';

const _excluded = ["label", "message", "status", "disabled", "id", "className", "alpha2Codes", "checkedPhoneCode", "value", "onChangeCallback", "onBlur", "onFocus", "isAbsolute", "closeOnClickOutside", "closeOnFocusOutside"];
const b = block('phone-input');
const PhoneInput = /*#__PURE__*/forwardRef(function PhoneInput(_ref, ref) {
  let {
      label,
      message,
      status,
      disabled = false,
      id,
      className = '',
      alpha2Codes,
      checkedPhoneCode,
      value,
      onChangeCallback,
      onBlur,
      onFocus,
      isAbsolute,
      closeOnClickOutside,
      closeOnFocusOutside
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedAlpha2Code, setSelectedAlpha2Code] = useState(checkedPhoneCode && getAlpha2FromPhoneCode(alpha2Codes, checkedPhoneCode) || getUserAlpha2(alpha2Codes) || alpha2Codes[0]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mask, setMask] = useState(COUNTRIES[selectedAlpha2Code].mask);
  const [inputValue, setInputValue] = useState(applyPhoneMask(mask, value || ''));
  const localRef = useRef(null);
  const [isInputActive, setIsInputActive] = useState(false);
  useEffect(() => {
    if (!checkedPhoneCode) {
      return;
    }
    const newAlpha2Code = getAlpha2FromPhoneCode(alpha2Codes, checkedPhoneCode);
    if (newAlpha2Code) {
      setSelectedAlpha2Code(newAlpha2Code);
    }
  }, [checkedPhoneCode]);
  const localRefFunction = element => {
    localRef.current = element;
    if (!ref) {
      return;
    }
    if (typeof ref === 'function') {
      ref(element);
      return;
    }
    ref.current = element;
  };
  const onCodeCheck = alpha2code => () => {
    setSelectedAlpha2Code(alpha2code);
    setMask(COUNTRIES[alpha2code].mask);
    setIsDropdownOpen(false);
    onChangeCallback(COUNTRIES[alpha2code].phoneCode, value);
  };
  const onInputChange = e => {
    const input = e.target;
    const previousSelectionStart = input.selectionStart || 0;
    const newValue = applyPhoneMask(mask, e.target.value) || '';
    const leftSubstring = input.value.slice(0, previousSelectionStart) || '';
    const leftSubstringWithMask = applyPhoneMask(mask, leftSubstring) || '';
    setCursorPosition(previousSelectionStart + (leftSubstringWithMask.length - leftSubstring.length));
    e.target.value = newValue;
    setInputValue(newValue);
    if (!onChangeCallback) {
      return undefined;
    }
    return onChangeCallback(COUNTRIES[selectedAlpha2Code].phoneCode, newValue);
  };
  const onBlurCallback = e => {
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    setIsInputActive(false);
  };
  const onFocusCallback = e => {
    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    setIsInputActive(true);
  };
  const onDropdownToggle = () => setIsDropdownOpen(currentValue => !currentValue);
  useEffect(() => {
    var _localRef$current;
    (_localRef$current = localRef.current) === null || _localRef$current === void 0 ? void 0 : _localRef$current.setSelectionRange(cursorPosition, cursorPosition);
  }, [inputValue]);
  return /*#__PURE__*/jsxs("div", {
    className: b({
      status,
      disabled
    }).mix(className),
    "aria-label": id,
    id: id,
    children: [/*#__PURE__*/jsx("label", {
      className: b('label'),
      htmlFor: "".concat(id, "-input"),
      children: label
    }), message && /*#__PURE__*/jsx("p", {
      "aria-live": "assertive",
      className: b('message'),
      children: message
    }), /*#__PURE__*/jsxs("div", {
      className: b('field', {
        active: isInputActive || isDropdownOpen
      }),
      children: [/*#__PURE__*/jsx(Dropdown, {
        isOpen: isDropdownOpen,
        onToggle: onDropdownToggle,
        closeOnClickOutside: closeOnClickOutside,
        closeOnFocusOutside: closeOnFocusOutside,
        color: "primary",
        disabled: disabled,
        id: "".concat(id, "-flags"),
        withArrow: true,
        isAbsolute: isAbsolute,
        buttonClassName: b('dropdown-button', {
          open: isDropdownOpen
        }),
        className: b('dropdown', {
          open: isDropdownOpen
        }),
        "aria-label": COUNTRIES[selectedAlpha2Code].name,
        list: /*#__PURE__*/jsx(PhoneInputList, {
          isOpen: isDropdownOpen,
          alpha2Codes: alpha2Codes,
          selectedAlpha2Code: selectedAlpha2Code,
          onCheck: onCodeCheck,
          id: "".concat(id, "-list")
        }),
        children: /*#__PURE__*/jsx("span", {
          className: b('list-icon').mix("fi fi-".concat(String(selectedAlpha2Code).toLowerCase())),
          id: "".concat(id, "-flag")
        })
      }), /*#__PURE__*/jsx("input", _objectSpread2({
        className: b('input'),
        disabled: disabled,
        id: "".concat(id, "-input"),
        onChange: onInputChange,
        "data-status": status,
        ref: localRefFunction,
        value: inputValue,
        placeholder: mask,
        onBlur: onBlurCallback,
        onFocus: onFocusCallback
      }, rest))]
    })]
  });
});
PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
